<template>
  <b-card no-body>
    <b-card-header class="d-flex justify-content-between align-items-center">
      <b-card-title class="text-secondary mb-0">
        {{$t(`message.latest_stats`)}}
      </b-card-title>

      <!-- Chart Type Selector -->
      <b-button-group size="sm">
        <b-button
          v-for="type in chartTypes"
          :key="type.value"
          :variant="chartType === type.value ? 'primary' : 'outline-primary'"
          @click="chartType = type.value"
        >
          {{ type.label }}
        </b-button>
      </b-button-group>
    </b-card-header>

    <b-card-body v-if="chartDataReady">
      <!-- BAR -->
      <chartjs-component-bar-chart
        v-if="chartType === 'bar'"
        :height="400"
        :data="chartData"
        :options="commonOptions"
      />

      <!-- HORIZONTAL BAR -->
      <chartjs-component-horizontal-bar-chart
        v-else-if="chartType === 'horizontalBar'"
        :height="400"
        :data="chartData"
        :options="horizontalOptions"
      />

      <!-- LINE -->
      <chartjs-component-line-chart
        v-else-if="chartType === 'line'"
        :height="400"
        :data="chartData"
        :options="commonOptions"
      />

      <!-- RADAR -->
      <chartjs-component-radar-chart
        v-else-if="chartType === 'radar'"
        :height="400"
        :data="chartData"
        :options="commonOptions"
      />
    </b-card-body>

    <b-card-body v-else class="text-center py-5">
      <b-spinner variant="dark" style="width: 3rem; height: 3rem;"></b-spinner>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BSpinner, BCard, BCardHeader, BCardBody, BCardTitle, BButtonGroup, BButton
} from 'bootstrap-vue'
import ChartjsComponentBarChart from './charts-components/ChartjsComponentBarChart.vue'
import ChartjsComponentHorizontalBarChart from './charts-components/ChartjsComponentHorizontalBarChart.vue'
import ChartjsComponentLineChart from './charts-components/ChartjsComponentLineChart.vue'
import ChartjsComponentRadarChart from './charts-components/ChartjsComponentRadarChart.vue'
import { performLogoutWithoutRedirect } from '@/auth/logout'

export default {
  components: {
    BSpinner, BCard, BCardHeader, BCardBody, BCardTitle, BButtonGroup, BButton,
    ChartjsComponentBarChart,
    ChartjsComponentHorizontalBarChart,
    ChartjsComponentLineChart,
    ChartjsComponentRadarChart,
  },
  data() {
    return {
      chartDataReady: false,
      chartType: 'bar', // default
      chartData: {
        labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
        datasets: [{
          data: [],
          backgroundColor: '#4abb9d',
          borderColor: '#4abb9d',
          borderWidth: 2,
        }]
      },
      commonOptions: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        scales: {
          yAxes: [{ ticks: { beginAtZero: true } }]
        },
        tooltips: {
          callbacks: {
            title: (tooltipItem, data) => {
              const fullMonths = ['January','February','March','April','May','June','July','August','September','October','November','December']
              return fullMonths[tooltipItem[0].index]
            }
          }
        }
      },
      horizontalOptions: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        scales: {
          xAxes: [{ 
            ticks: { beginAtZero: true },
            gridLines: { display: true }
          }],
          yAxes: [{ 
            gridLines: { display: false }
          }]
        },
        tooltips: {
          callbacks: {
            title: (tooltipItem, data) => {
              const fullMonths = ['January','February','March','April','May','June','July','August','September','October','November','December']
              return fullMonths[tooltipItem[0].index]
            }
          }
        }
      },
      chartTypes: [
        { value: 'bar',           label: '│ Bar' },
        { value: 'horizontalBar', label: '— Bar' },
        { value: 'line',          label: '📈 Line' },
        { value: 'radar',         label: '🌀 Radar' }
      ]
    }
  },
  computed: {
    cookie() { return window?.$cookies?.get("jwt") ?? null },
    token_id() { return this.$store.state.app?.userData?.id ?? null }
  },
  async created() {
    await this.fetchMonthlyData()
  },
  methods: {
    async fetchMonthlyData() {
      const payload = { 
        token: this.token_id, 
        jwt: this.cookie 
      }
      try {
        const response = await fetch('https://api-quote.safetravel.today/v2/app/sum_amount', {
          method: 'POST',
          headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        })
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        const res = await response.json()
        if (res === 'not_valid') {
          performLogoutWithoutRedirect(this.$store)
          return
        }
        const monthlyData = Array(12).fill(0)
        res.forEach(r => {
          if (r.month >= 1 && r.month <= 12) {
            monthlyData[r.month - 1] = parseFloat(r.total) || 0
          }
        })
        this.chartData.datasets[0].data = monthlyData
      } catch (err) {
        console.error('Fetch error:', err)
      } finally {
        this.chartDataReady = true
      }
    }
  }
}
</script>